import React, { useState, useRef, useEffect, useCallback, useContext } from 'react'
import { pickDataAttrs } from '../../../utils/dataAttributes'
import {
  ChatbotContainer,
  InputSection,
  ChatSection,
  ChatContent,
  ClearButtonFull,
  ClearButtonFullWrapper,
  ClearButtonIconWrapper,
  SearchMobileWrapper,
  SearchDesktopWrapper,
  InputContentWrapper,
  InputContentInnerWrapper,
  ExampleQuestionsContainer,
  LoadingIndicatorWrapper,
  hexColors,
  DisclaimerContainer,
} from './Styles'
import {
  A11yText,
  Button,
  Search,
  IconButton,
  ActivityIndicator,
  Spacer,
  ExpandCollapse,
} from '@telus-uds/components-web'
import {
  createUserMessage,
  askChatbot,
  createBotMessage,
  createErrorMessage,
  submitFeedback,
  shouldShowDisclaimer,
  FeedbackPayload,
} from './chatbotUtils'
import { Refresh, ArrowUpBold } from '@telus-uds/palette-allium/build/web/icons'
import { getWelcomeMessages } from './welcomeMessageUtils'
import type { ChatHistoryEntry, Message, WelcomeMessage } from './types'
import MessageItem from './MessageItem'
import WelcomeMessageItem from './WelcomeMessageItem'
import { SiteBuilderContext } from 'src/siteBuilder/renderer/context/SiteBuilderContext'
export type { ApiResponse, ChatHistoryEntry, Message } from './types'

type ChatbotUIProps = {
  placeholder?: string
  placeholderDesktop?: string
  exampleQuestions?: string[]
  welcomeMessage?: React.ReactNode
  secondaryWelcomeMessage?: React.ReactNode
  welcomeTagLabel?: string
  userTagLabel?: string
  clearChatButtonLabel?: string
  assistantTagLabel?: string
  feedbackText?: string
  addCommentCtaText?: string
  feedbackConfirmationText?: string
  feedbackPlaceholderText?: string
  disclaimerTitle?: React.ReactNode
  disclaimerBody?: React.ReactNode
  locale?: string
}

/** Normalises any locale string to the two values the chatbot API accepts. */
const normalizeLocale = (value?: string | null): 'en' | 'fr' => (value?.toLowerCase().startsWith('fr') ? 'fr' : 'en')

const accentColor = hexColors('TELUS Purple')

const ChatbotUI = ({
  placeholder,
  placeholderDesktop,
  exampleQuestions = [],
  welcomeMessage = undefined,
  secondaryWelcomeMessage = undefined,
  welcomeTagLabel,
  userTagLabel,
  clearChatButtonLabel,
  assistantTagLabel,
  feedbackText,
  addCommentCtaText,
  feedbackConfirmationText,
  feedbackPlaceholderText,
  disclaimerTitle,
  disclaimerBody,
  locale,
  ...rest
}: ChatbotUIProps) => {
  const {
    locale: { language: contextLanguage },
  } = useContext(SiteBuilderContext)

  const resolvedLocale: 'en' | 'fr' = normalizeLocale(locale ?? contextLanguage)
  const clearChatA11yLabel = clearChatButtonLabel ?? (resolvedLocale === 'fr' ? 'Effacer le chat' : 'Clear chat')
  const sourcesLabel = resolvedLocale === 'fr' ? 'Sources :' : 'Sources:'
  const thumbsUpLabel = resolvedLocale === 'fr' ? 'Pouce en haut' : 'Thumbs up'
  const thumbsDownLabel = resolvedLocale === 'fr' ? 'Pouce en bas' : 'Thumbs down'
  const localizedCommentSubmitLabel = resolvedLocale === 'fr' ? 'Soumettre' : 'Submit'
  const localizedCommentCancelLabel = resolvedLocale === 'fr' ? 'Annuler' : 'Cancel'
  const tableAccessibilityLabel = resolvedLocale === 'fr' ? 'Tableau defilable' : 'Scrollable table'

  const welcomeMessages: WelcomeMessage[] = getWelcomeMessages(welcomeMessage, secondaryWelcomeMessage)

  const [chatHistory, setChatHistory] = useState<ChatHistoryEntry[]>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // Ref to the ChatSection scroll container — scrolled directly to avoid page scroll.
  const chatSectionRef = useRef<HTMLDivElement>(null)
  // Skips the auto-scroll on the initial render so the user sees the top of the chat first.
  const isInitializedRef = useRef(false)

  const handleClearChat = () => {
    setChatHistory([])
    setMessages([])
    setInputValue('')
  }

  /**
   * Scrolls the ChatSection container (not the page) to its bottom so the latest
   * message or loading spinner is always visible above the sticky InputSection.
   */
  const scrollToBottom = useCallback(() => {
    if (chatSectionRef.current) {
      chatSectionRef.current.scrollTop = chatSectionRef.current.scrollHeight
    }
  }, [])

  // Trigger auto-scroll whenever a new message is added or loading state changes,
  // but skip the very first render (welcome messages).
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      return
    }
    scrollToBottom()
  }, [chatHistory, messages, isLoading, scrollToBottom])

  /** Normalises the Search component's polymorphic onChange value to a plain string. */
  const handleSearchChange = (value: string | React.ChangeEvent<HTMLInputElement>) =>
    setInputValue(typeof value === 'string' ? value : value?.target?.value ?? '')

  /**
   * Core function to handle sending a query.
   * Returns true if the query was sent, false if skipped (blank input or already loading).
   */
  const sendQuery = async (query: string): Promise<boolean> => {
    if (!query.trim() || isLoading) return false

    const trimmedQuery = query.trim()
    const userMessage = createUserMessage(trimmedQuery, userTagLabel)
    setMessages((prev) => [...prev, userMessage])
    setIsLoading(true)

    try {
      const data = await askChatbot(trimmedQuery, resolvedLocale, chatHistory)
      const botMessage = createBotMessage(data, trimmedQuery, assistantTagLabel)

      setMessages((prev) => [...prev, botMessage])
      setChatHistory((prev) => [
        ...prev,
        { role: 'user', content: trimmedQuery },
        { role: 'assistant', content: botMessage.content },
      ])
    } catch (err) {
      console.error('Error sending chatbot query:', err)
      setMessages((prev) => [...prev, createErrorMessage(assistantTagLabel, resolvedLocale)])
    } finally {
      setIsLoading(false)
    }

    return true
  }

  const handleSubmit = () => {
    sendQuery(inputValue)
      .then((sent) => {
        if (sent) setInputValue('')
      })
      .catch((err) => console.error('Unexpected error in sendQuery:', err))
  }

  const handleMessageFeedback = (payload: FeedbackPayload) => {
    submitFeedback(payload).catch((err) => console.error('Error submitting feedback:', err))
  }

  const handleSendExample = (exampleText: string) => {
    sendQuery(exampleText).catch((err) => console.error('Error sending example question:', err))
  }

  return (
    <ChatbotContainer data-testid="chatbot-ui-testid" {...pickDataAttrs(rest)}>
      <ChatSection ref={chatSectionRef}>
        <ChatContent role="log" aria-live="polite" aria-label="Chat conversation messages">
          {/* Welcome messages — always visible, not affected by clear chat */}
          {welcomeMessages.map((message, idx) => (
            <WelcomeMessageItem
              key={message.id}
              message={message}
              welcomeTagLabel={idx === 0 ? welcomeTagLabel : undefined}
            />
          ))}

          {/* Example questions — shown after welcome messages, before any conversation */}
          {exampleQuestions.length > 0 && (
            <ExampleQuestionsContainer>
              {exampleQuestions.map((example, idx) => (
                <Button
                  key={idx}
                  tokens={{ borderColor: accentColor, color: accentColor, fontWeight: '300' }}
                  onPress={() => handleSendExample(example)}
                >
                  {example}
                </Button>
              ))}
            </ExampleQuestionsContainer>
          )}

          {/* Conversation messages (user queries and bot responses) */}
          {messages.map((message) => (
            <MessageItem
              key={message.id}
              message={message}
              tableAccessibilityLabel={tableAccessibilityLabel}
              sourcesLabel={sourcesLabel}
              feedbackText={feedbackText}
              addCommentCtaText={addCommentCtaText}
              feedbackConfirmationText={feedbackConfirmationText}
              feedbackPlaceholderText={feedbackPlaceholderText}
              commentSubmitLabel={localizedCommentSubmitLabel}
              commentCancelLabel={localizedCommentCancelLabel}
              thumbsUpLabel={thumbsUpLabel}
              thumbsDownLabel={thumbsDownLabel}
              onFeedback={handleMessageFeedback}
            />
          ))}

          {isLoading && (
            <LoadingIndicatorWrapper>
              <ActivityIndicator label="" />
            </LoadingIndicatorWrapper>
          )}

          {shouldShowDisclaimer(messages, isLoading, disclaimerTitle, disclaimerBody) && (
            <DisclaimerContainer>
              <ExpandCollapse>
                {(expandProps) => (
                  <ExpandCollapse.Panel {...expandProps} panelId="first" control={disclaimerTitle ?? null}>
                    {disclaimerBody ?? null}
                  </ExpandCollapse.Panel>
                )}
              </ExpandCollapse>
            </DisclaimerContainer>
          )}
        </ChatContent>
      </ChatSection>

      <InputSection>
        <InputContentWrapper>
          <InputContentInnerWrapper>
            <ClearButtonFullWrapper>
              <ClearButtonFull
                icon={Refresh}
                tokens={{ borderColor: accentColor, color: accentColor, fontWeight: '700', iconSpace: 3 }}
                onPress={handleClearChat}
              >
                {clearChatButtonLabel ?? clearChatA11yLabel}
                <A11yText text={`, ${clearChatA11yLabel}`} />
              </ClearButtonFull>
              <Spacer space={3} direction="row" />
            </ClearButtonFullWrapper>

            <ClearButtonIconWrapper>
              <IconButton
                icon={Refresh}
                tokens={{ borderColor: accentColor, iconColor: accentColor }}
                variant={{ size: 'large', style: 'contained' }}
                onPress={handleClearChat}
                accessibilityLabel={clearChatA11yLabel}
              />
              <Spacer space={2} direction="row" />
            </ClearButtonIconWrapper>

            <SearchMobileWrapper>
              <Search
                placeholder={placeholder}
                value={inputValue}
                onChange={handleSearchChange}
                onSubmit={handleSubmit}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                tokens={{ submitButtonIcon: ArrowUpBold }}
              />
            </SearchMobileWrapper>

            <SearchDesktopWrapper>
              <Search
                placeholder={placeholderDesktop}
                value={inputValue}
                onChange={handleSearchChange}
                onSubmit={handleSubmit}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                tokens={{ submitButtonIcon: ArrowUpBold }}
              />
            </SearchDesktopWrapper>
          </InputContentInnerWrapper>
        </InputContentWrapper>
      </InputSection>
    </ChatbotContainer>
  )
}

export default ChatbotUI
